import { Box, Button, Center, Flex, Image, Stack, Text, useDisclosure, useMediaQuery, useToast, VStack } from "@chakra-ui/react";
import { useGetTransferAddressQuery, useGetTransferHistoryByIdQuery, useMarkAsPaidMutation, useTransferInMutation } from "apis";
import {
  ActionBar,
  AddFiatAccount,
  AddMobileMoney,
  CloseWindow,
  CopyButton,
  ExchangeInfo,
  FormInfo,
  GetFiatAccount,
  Layout,
  PageMotion,
  PaymentExchangeInfoDetail,
  PositiveInfo,
  PoweredBy,
  QuoteTimer,
  UploadProofModal,
} from "components";
import { useAuthToken, useCopy, useForm, usePartialState } from "hooks";
import { IStatefulUI, PageProps } from "interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { selectForm, setNewTransferId } from "store/slices";
import { triggerWeb3, handleCancelPayment, toCoinLocale } from "utils";
import { getCoinPrecision, MathRound } from "utils/toPrecision";
import ls from "utils/secureStorage";
import configs from "config";
import CircleLoader from "components/Loader/CircularLoader";

export interface PaymentProps extends PageProps {}
export interface ViewProps extends PaymentProps, IStatefulUI<IState> {}

const showTransferTimer = true;
interface IState {
  receipt: File;
  isLoading: boolean;
}

export function Payment(props: PaymentProps) {
  const tabType: "buy" | "sell" = ls.get("tabType");
  return (
    <PageMotion key="Payment" px="4px" minH={{ base: "100vh", md: "96vh" }}>
      <Box maxW="500px" mx="auto">
        <Layout>
          {/* Provide Fiat Account for Receiving Payment  */}
          <ActionBar
            title={tabType === "sell" ? "Provide Your Fiat Account Details" : "Payment Information"}
            textAlign="center"
            _back={{ visibility: "hidden" }}
          />
          <PaymentMethod tabType={tabType} />
        </Layout>

        <PoweredBy bottom={{ base: "2%", md: "5.75%" }} />
      </Box>
    </PageMotion>
  );
}

interface IProps {
  tabType: "sell" | "buy";
}
function PaymentMethod({ tabType }: IProps) {
  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { quoteRes, transferId, transferData, setTransferId, setTransferData } = useForm();

  const account = useMemo(() => quoteRes?.fiatAccount, [quoteRes]);

  const quote = useMemo(() => quoteRes?.quote, [quoteRes]);

  const [state, set] = usePartialState<IState>();
  const [markAsPaid] = useMarkAsPaidMutation();

  const { timerExpired } = useSelector(selectForm);

  const handleContinue = async (e: any) => {
    e.preventDefault();
    if (!state?.receipt && !transferId && !account?.reference) return;

    try {
      set({ isLoading: true });

      if (!!transferId) {
        await markAsPaid({ transferId, uploadUrl: "" }).unwrap();
        onOpen();
      }

      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false });
    }
  };

  const URL = ls.get(configs.PERSIST_URL);
  const qr = ls.get("qrcode");

  console.log(qr, "QR");

  useEffect(() => {
    if (!quoteRes || (!transferId && (tabType === "buy" || !tabType))) navigate(`/${URL}`, { replace: true });
  });

  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const handleCancel = async () => {
    setIsCancelLoading(true);

    await handleCancelPayment(transferId!);
  };

  const cryptoAmountMinusFee = useMemo(() => {
    const cryptoAmount = quote?.cryptoAmount ?? 0;
    const cryptoAmountMinusFees = Number(cryptoAmount) - Number(quote?.processingFee ?? 0) - Number(quote?.networkFee ?? 0);
    const roundedAmount = MathRound(cryptoAmountMinusFees, getCoinPrecision(quote?.cryptoType || "", 12));
    return roundedAmount;
  }, [quote]);

  const { authToken } = useAuthToken();

  const [transferOut, { isLoading: transferLoading, isSuccess }] = useTransferInMutation();

  const method = useMemo(() => (ls.get("method") as string) ?? "", []);

  const createTransfer = useCallback(
    async (quoteId: string, fiatAccountId: string, cryptoType: string) => {
      try {
        const transferResult = await transferOut({
          quoteId,
          fiatAccountId,
          type: "out",
          token: authToken!,
        }).unwrap();

        setTransferId(transferResult?.transferId);
        setTransferData(transferResult);

        const res =
          cryptoType === "cusd"
            ? await triggerWeb3(transferResult.transferAddress, quote?.cryptoAmount!, method, cryptoType)
            : undefined;

        if (typeof res === "string" || typeof res?.data === "string") navigate(`/transfer-history/${transferResult.transferId}`);

        // if (res?.code === 4001 || res?.info?.error) {
        // await handleCancelPayment(transferResult.transferId);
        // }

        if (res?.info?.error) {
          toast({
            position: isMobile ? "top" : "bottom-right",
            title: "Error",
            duration: 9000,
            isClosable: true,
            status: "error",
            description: res.info.error?.data?.message,
          });
        }

        console.log(res, "THE ERROR RESPONSE");
      } catch (error: any) {
        if (error?.data?.error === "KycRequired") {
          navigate("/kyc", { replace: true });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authToken]
  );

  const { newTransferId } = useSelector(selectForm);

  const [fiatAcctId, setFiatId] = useState("");

  const handleFiatAccountId = useCallback(
    (value: string, cryptoType: string) => {
      setFiatId(value);
      ls.set("fiatAccountId", value);

      if (value !== "new-account" && value.length > 5) {
        createTransfer(quote?.quoteId!, value, cryptoType.toLowerCase());
      }
    },
    [createTransfer, quote?.quoteId]
  );

  // const tabType: "buy" | "sell" = ls.get("tabType");

  const { data: transferHistory } = useGetTransferHistoryByIdQuery(transferId!, {
    skip: transferId && tabType === "sell" ? false : true,
    pollingInterval: 5000,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      transferHistory?.direction === "out" &&
      (transferHistory?.status === "TransferComplete" || transferHistory?.status === "TransferFailed")
    ) {
      navigate(`/transfer-history/${transferId}`, { replace: true });
    }
  }, [navigate, transferHistory, transferId]);

  useEffect(() => {
    if (tabType === "sell" && newTransferId) {
      setFiatId(newTransferId);
      handleFiatAccountId(newTransferId, quote?.cryptoType!);

      dispatch(setNewTransferId(null));
    }
  }, [newTransferId, tabType, handleFiatAccountId, quote?.cryptoType, dispatch]);

  const { data: addressData } = useGetTransferAddressQuery(transferId!, {
    skip: transferId ? false : true,
  });

  const { updateIntialValue, onCopy } = useCopy(addressData?.transferAddress ?? "");

  const handleCopy = () => {
    onCopy();
    updateIntialValue(addressData?.transferAddress!);
  };

  return (
    <Box>
      <PositiveInfo
        mt={{ base: "8px", md: "32px" }}
        mb={{ base: "8px", md: "16px" }}
        py={{ base: "4px" }}
        border="1px solid transparent"
        borderBottomColor="#F2F4F3"
        textAlign="center"
      >
        {tabType === "sell" &&
          transferId &&
          `Transfer ${toCoinLocale(quote?.cryptoType!.toLowerCase()!)} to the address provided below`}

        {tabType === "sell" && !transferId && `Complete your transaction using the details below`}

        {(tabType === "buy" || !tabType) &&
          `Complete your transaction using the details below and ensure to use the transaction reference in your payment narration`}
      </PositiveInfo>

      {timerExpired ? (
        <Center flexDir="column" minH="60vh">
          <Text color="#ED1C00">Payment window timedout</Text>

          <CloseWindow />
        </Center>
      ) : (
        <Stack gap="0 !important" my="0 !important" px={{ base: "0", "2sm": "3px" }}>
          {tabType === "buy" || !tabType ? (
            <ExchangeInfo gridGap="10px">
              <PaymentExchangeInfoDetail
                title="Fiat Amount:"
                description={`${Number(quote?.fiatAmount).toLocaleString()}` ?? ""}
                secondDescription={quote?.fiatType}
                canCopy
              />

              <PaymentExchangeInfoDetail
                title="Crypto Amount:"
                description={`${Number(cryptoAmountMinusFee).toLocaleString()} ${quote?.cryptoType}` ?? ""}
              />

              <PaymentExchangeInfoDetail title="Bank Name:" description={account?.networkOrBankCode ?? ""} />
              <PaymentExchangeInfoDetail title="Account Name:" description={account?.accountName ?? ""} />
              <PaymentExchangeInfoDetail canCopy title="Account Number:" description={account?.accountNumber ?? ""} />
              <PaymentExchangeInfoDetail canCopy title="Transaction Reference:" description={account?.reference ?? ""} />
            </ExchangeInfo>
          ) : (
            <Box minH="30vh">
              <GetFiatAccount
                isDisabled={transferLoading || isSuccess}
                onChange={(e) => handleFiatAccountId(e.target.value, quote?.cryptoType!)}
                fiatType={quote?.fiatType!}
              />

              {transferLoading && <CircleLoader my="4px" />}

              {fiatAcctId && fiatAcctId !== "new-account" && (
                <Box opacity={transferLoading ? 0.5 : 1}>
                  <ExchangeInfo gridGap="10px">
                    <PaymentExchangeInfoDetail
                      title="Fiat Amount:"
                      description={`${quote?.fiatType} ${Number(quote?.fiatAmount).toLocaleString()}` ?? ""}
                    />

                    <PaymentExchangeInfoDetail
                      title="Crypto Amount:"
                      description={`${Number(quote?.cryptoAmount).toLocaleString()}` ?? ""}
                      thirdDescription={quote?.cryptoType}
                      canCopy
                    />

                    {transferData?.transferAddress && qr !== "1" && (
                      <PaymentExchangeInfoDetail
                        title="Transfer Address:"
                        canCopy
                        _title={{
                          flexShrink: 0,
                        }}
                        description={transferData?.transferAddress ?? ""}
                        _description={{
                          maxWidth: { sm: "140px", md: "165px" },
                          fontSize: { sm: "12px", md: "14px" },
                          fontWeight: "600",
                        }}
                        // hideInfo
                      />
                    )}

                    {transferData?.transferMemo && (
                      <PaymentExchangeInfoDetail canCopy title="Transfer Memo:" description={transferData?.transferMemo ?? ""} />
                    )}

                    {transferData?.transferTagNumber && (
                      <PaymentExchangeInfoDetail
                        canCopy
                        title="Transfer Tag Number:"
                        description={transferData?.transferTagNumber ?? ""}
                      />
                    )}

                    {addressData && qr === "1" && (
                      <Center flexDir="column">
                        <Image src={addressData.qrCode} width="200px" height="200px" borderRadius="8px" />

                        <Box py="4px" mt="12px" maxWidth="400px" width="100%">
                          <Flex
                            width="100%"
                            mx="auto"
                            alignItems="center"
                            padding="12px"
                            borderRadius="8px"
                            justifyContent="space-between"
                            bg="#6FBE45"
                            gap="4px"
                          >
                            <Text
                              maxWidth={{ base: "190px", "1sm": "240px", md: "250px" }}
                              fontSize={{ base: "14px", "1sm": "14px", md: "16px" }}
                              textAlign="center"
                              fontWeight="600"
                              color="#fff"
                            >
                              {addressData?.transferAddress}
                            </Text>

                            <CopyButton
                              _hover={{ color: "#fff" }}
                              bg="#0D4740"
                              color="#fff"
                              onCopy={handleCopy}
                              onClick={handleCopy}
                            />
                          </Flex>
                        </Box>
                      </Center>
                    )}
                  </ExchangeInfo>
                </Box>
              )}

              {fiatAcctId === "new-account" && (
                <>
                  {quote?.fiatType.toLowerCase() === "ngn" ? (
                    <AddFiatAccount fiatType="ngn" />
                  ) : (
                    <AddMobileMoney fiatType={quote?.fiatType!} />
                  )}
                </>
              )}
            </Box>
          )}

          {(tabType === "buy" || !tabType || tabType !== "sell") && (
            <>
              <Text mt="8px" fontWeight="600">
                Please Note
              </Text>
              <FormInfo
                info=""
                isInvalid
                mt={{ base: "4px !important", md: "4px !important" }}
                description={
                  <>
                    <Box as="ul">
                      <Box as="li" mb="4px">
                        Do not include any crypto keywords in the narration
                      </Box>

                      <Box as="li">After payment, ensure to click the 'I have paid' button to avoid transaction cancellation</Box>
                    </Box>
                    {/* Please include the Transaction Reference above in your payment narration. DO NOT use any crypto-related keywords
                  in the narration */}
                  </>
                }
                // _description={{ textAlign: "center" }}
              />
            </>
          )}

          {isSuccess && tabType === "sell" && (
            <Center flexDir="column" margin="8px 0 0">
              {qr !== "1" && <Box my="1rem"></Box>}

              <CircleLoader />
              <Text fontSize="24px" fontWeight="500">
                Waiting for crypto...
              </Text>
            </Center>
          )}

          {showTransferTimer && !!transferData && (
            <QuoteTimer
              title="Payment should be made within"
              mt="12px !important"
              time={transferData?.guaranteedUntil}
              checkExpire
            />
          )}

          {(tabType === "buy" || !tabType) && (
            <>
              <Box as="form" onSubmit={handleContinue}>
                <VStack mt={{ base: "20px !important", md: "60px !important" }}>
                  <Button
                    w="100%"
                    minW={{ base: "auto", "2sm": "400px" }}
                    type="submit"
                    isLoading={!!state?.isLoading}
                    textTransform="initial"
                    fontFamily="var(--bitmama-fonts-heading)"
                    _hover={{
                      _disabled: {
                        opacity: 0.3,
                      },
                    }}
                  >
                    I have Paid
                  </Button>
                </VStack>
              </Box>
            </>
          )}

          <Button
            variant="transparent"
            minW={{ base: "auto", "2sm": "400px" }}
            w="100%"
            onClick={handleCancel}
            isLoading={isCancelLoading}
            fontWeight="500"
            _focus={{ shadow: "outline", textDecoration: "underline" }}
          >
            Cancel Payment
          </Button>
        </Stack>
      )}

      {isOpen && <UploadProofModal transferId={transferId!} isOpen={isOpen} onClose={onClose} />}
    </Box>
  );
}
